import type { Recipe } from '../types';

// No-bake peanut butter protein bites. One bowl, no oven, rolled and frozen.
// Built on the classic oats + nut butter + honey energy ball, with whey
// concentrate for protein, ground flax for fiber, and less honey than most.

export const peanutButterProteinBites: Recipe = {
  slug: 'peanut-butter-protein-bites',
  title: 'Peanut Butter Protein Bites',
  meal: 'breakfast',
  category: 'snack',
  types: ['no-bake'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'make-ahead', 'high-protein'],
  yield: 'about 24 bites — 12 servings',
  activeTime: '20 min',
  totalTime: '50 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    "Oats, peanut butter, honey and protein powder stirred in one bowl, rolled into bite-size balls and frozen. About 5 grams of protein each. Grab two on the way out the door or after a workout — they are ready to eat 10 minutes out of the freezer.",

  flavorNotes:
    "Like a soft, chewy peanut butter cookie dough with a few chocolate chips — sweet but not candy-sweet, salty-nutty, with a hint of cinnamon and vanilla. The texture should be chewy and hold together when you bite it, not crumble and not stick to your teeth. If they taste chalky, the protein powder is the issue: use whey concentrate, not isolate.",

  ingredients: [
    {
      id: 'peanut-butter',
      name: "Crazy Richard's natural peanut butter",
      total: '1 cup',
      prep: 'stirred very well first — the oil on top is what binds the bites',
      uses: [{ step: 1, amount: '1 cup' }],
    },
    {
      id: 'honey',
      name: 'Honey',
      total: '½ cup',
      uses: [{ step: 1, amount: '½ cup' }],
    },
    {
      id: 'vanilla',
      name: 'Pure vanilla extract',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'oats',
      name: 'Old-fashioned rolled oats',
      total: '2 cups',
      prep: 'rolled or quick oats — NOT steel-cut',
      uses: [{ step: 2, amount: '2 cups' }],
    },
    {
      id: 'protein-powder',
      name: 'Protein powder, vanilla or chocolate',
      total: '½ cup whey concentrate, OR ⅓ cup plant-based',
      prep: 'two versions, amounts differ — whey CONCENTRATE is the one to buy (not isolate); plant-based soaks up more liquid, so use less and add water, see step 2',
      uses: [{ step: 2, amount: '½ cup (whey) or ⅓ cup (plant)', note: 'about 2 scoops of whey' }],
    },
    {
      id: 'ground-flax',
      name: 'Ground flaxseed',
      total: '3 tbsp',
      prep: 'ground (flaxseed meal), not whole seeds',
      shop: "Bob's Red Mill golden flaxseed meal, 16 oz",
      uses: [{ step: 2, amount: '3 tbsp' }],
    },
    {
      id: 'cinnamon',
      name: 'Ground cinnamon',
      total: '½ tsp',
      uses: [{ step: 2, amount: '½ tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '¼ tsp',
      uses: [{ step: 2, amount: '¼ tsp' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '1–3 tbsp',
      prep: 'only if the mix is dry',
      uses: [{ step: 2, amount: '1–3 tbsp', note: 'mainly with plant protein' }],
    },
    {
      id: 'chocolate-chips',
      name: 'Mini chocolate chips',
      total: '⅓ cup',
      prep: 'regular size works too',
      uses: [{ step: 3, amount: '⅓ cup' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Mix the wet',
      time: '3 min',
      ingredientIds: ['peanut-butter', 'honey', 'vanilla'],
      body:
        "In a big bowl stir {1 cup|peanut-butter} of peanut butter, {½ cup|honey} of honey and {1 tsp|vanilla} of vanilla until smooth. If the peanut butter is stiff from the fridge, microwave it with the honey for 20 seconds first.",
    },
    {
      n: 2,
      title: 'Stir in the dry',
      time: '3 min',
      ingredientIds: ['oats', 'protein-powder', 'ground-flax', 'cinnamon', 'salt', 'water'],
      body:
        "Add {2 cups|oats} of oats, the protein powder ({½ cup} of whey concentrate, or {⅓ cup} of plant-based), {3 tbsp|ground-flax} of ground flax, {½ tsp|cinnamon} of cinnamon and {¼ tsp|salt} of salt. Stir hard with a sturdy spoon until no dry oats or powder are left. It will be thick. With plant-based protein, stir in {1 tablespoon|water} of water at a time until it comes together.",
      tip:
        "Plant protein drinks up much more liquid than whey — at the whey amount the bites come out dry and crumbly. That is why it gets less powder plus water.",
    },
    {
      n: 3,
      title: 'Rest, then add the chips',
      time: '10 min',
      critical: true,
      ingredientIds: ['chocolate-chips'],
      body:
        "Let the bowl sit 10 minutes so the oats and flax soak up moisture — the mix firms up and gets much easier to roll. Then fold in {⅓ cup|chocolate-chips} of chocolate chips.",
    },
    {
      n: 4,
      title: 'Squeeze test',
      time: '1 min',
      ingredientIds: [],
      body:
        "Squeeze a spoonful in your fist. It should hold together in a ball and feel like soft cookie dough. Too sticky to roll: stir in 1 to 2 more tablespoons of oats. Too crumbly: add water a teaspoon at a time.",
    },
    {
      n: 5,
      title: 'Roll',
      time: '10 min',
      ingredientIds: [],
      body:
        "Scoop with a 1-tablespoon cookie scoop or a spoon and roll each into a ball between your palms. Set them on a parchment-lined sheet pan. Slightly damp hands stop the mix from sticking.",
    },
    {
      n: 6,
      title: 'Firm up and bag',
      time: '30 min',
      ingredientIds: [],
      body:
        "Freeze the pan 30 minutes until the bites are firm — a flash freeze, so they won't stick together. Move them into a freezer bag or airtight container. Keep a week's worth in the fridge and the rest in the freezer.",
    },
  ],

  freezing:
    "Flash freeze on the sheet pan for 30 minutes, then into a gallon freezer bag with the air pressed out. Up to 3 months in the freezer, or 2 weeks in an airtight container in the fridge.",

  reheat: [
    {
      method: 'From the freezer',
      best: true,
      detail:
        "No heating needed. Let them sit 5 to 10 minutes on the counter and they are soft and chewy. Straight from the freezer they are firm and fudgy — some people like that better.",
    },
    {
      method: 'From the fridge',
      detail:
        "Ready to eat as is.",
    },
  ],

  glossary: [
    {
      term: 'whey concentrate',
      plain:
        "The common, cheaper kind of whey protein powder, made from milk. It has a little fat and blends smoothly. Isolate is more refined and tastes chalky and dry in no-bake snacks.",
    },
    {
      term: 'ground flax',
      plain:
        "Flaxseeds ground into a meal. Adds fiber and healthy fats and helps hold the bites together. Whole flaxseeds pass through your body undigested, so buy it ground.",
    },
    {
      term: 'flash freeze',
      plain:
        "Freezing pieces spread out on a pan before bagging them, so they freeze separately instead of in one solid clump.",
    },
  ],

  inspiration: [
    { label: 'Modern Honey — No-bake protein energy balls', url: 'https://modernhoney.com/no-bake-protein-energy-balls' },
    { label: 'The Healthy Maven — Peanut butter oatmeal energy balls', url: 'https://www.thehealthymaven.com/peanut-butter-oatmeal-energy-balls' },
    { label: 'Healthy Fitness Meals — No-bake protein energy bites', url: 'https://healthyfitnessmeals.com/no-bake-protein-energy-bites' },
  ],

  whereWeVeered:
    "Modern Honey's ratio is the base (2 cups oats, 1 cup nut butter, ½ cup protein powder). We cut the honey from ¾ cup to ½ cup to lower the sugar, borrowed ground flax and the rest-before-rolling step from The Healthy Maven, and added cinnamon and a pinch of salt. Water instead of milk for loosening, so nobody buys a half gallon for two tablespoons. Plant-protein amounts are written in, the same way as the banana oat muffins.",
};
